import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { useAuth } from '../context/AuthContext';
import TourBookingForm from './TourBookingForm';
import LeadDetailModal from './LeadDetailModal';
import FollowUpManager from './FollowUpManager';
import TourQuotationPDF from './TourQuotationPDF';
import NewLeadNotificationPopup from './NewLeadNotificationPopup';
import assignmentNotificationService from '../services/assignmentNotificationService';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Area, AreaChart, RadialBarChart, RadialBar
} from 'recharts';

const SalesDashboard = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddLead, setShowAddLead] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [activeTab, setActiveTab] = useState('analytics'); // 'analytics', 'tour_booking', 'other_services', 'forwarded', or 'followUps'
  const [analytics, setAnalytics] = useState({
    tourBooking: { total: 0, forwarded: 0, conversionRate: 0 },
    otherServices: { total: 0, assigned: 0, completed: 0, pending: 0, completionRate: 0 },
    overall: { totalLeads: 0, activeLeads: 0, responseTime: 0, successRate: 0 }
  });
  const [forwardedLeads, setForwardedLeads] = useState([]);
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [selectedLeadForFollowUp, setSelectedLeadForFollowUp] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedLeadForAssign, setSelectedLeadForAssign] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [quotations, setQuotations] = useState([]);
  const [loadingQuotations, setLoadingQuotations] = useState(false);
  const [showQuotationPDF, setShowQuotationPDF] = useState(false);
  const [selectedQuotationForView, setSelectedQuotationForView] = useState(null);
  const [showRevisionRequest, setShowRevisionRequest] = useState(false);
  const [selectedQuotationForRevision, setSelectedQuotationForRevision] = useState(null);
  const [revisionData, setRevisionData] = useState({
    changes: '',
    urgency: 'normal',
    notes: ''
  });
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedQuotationForReject, setSelectedQuotationForReject] = useState(null);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [openQuotationDropdown, setOpenQuotationDropdown] = useState(null); // For 3-dot dropdown

  // Initial load - fetch leads and employees only once
  useEffect(() => {
    fetchLeads();
    fetchEmployees();
  }, []); // Empty dependency - only run once on mount

  // Close quotation dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openQuotationDropdown && !event.target.closest('.quotation-dropdown-container')) {
        setOpenQuotationDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openQuotationDropdown]);

  // Fetch quotations only when switching to quotations tab
  const [quotationsLoaded, setQuotationsLoaded] = useState(false);

  // Helper function to refresh quotations (used after accept/reject/delete actions)
  const refreshQuotations = () => {
    setQuotationsLoaded(false); // This will trigger useEffect to refetch
  };

  useEffect(() => {
    if (activeTab === 'quotations' && !quotationsLoaded) {
      // Fetch quotations directly here
      const doFetch = async () => {
        try {
          setLoadingQuotations(true);
          const token = localStorage.getItem('token') ||
            localStorage.getItem('companyToken') ||
            localStorage.getItem('employeeToken');

          const currentUserId = user?.id || user?._id;
          const response = await axios.get('/leads', {
            headers: { Authorization: `Bearer ${token}` }
          });

          let allLeads = [];
          if (Array.isArray(response.data)) {
            allLeads = response.data;
          } else if (response.data.leads) {
            allLeads = response.data.leads;
          }

          // Filter leads with quotations
          const leadsWithQuotations = allLeads.filter(lead => {
            const isCreatedByCurrentUser = lead.createdBy === currentUserId ||
              lead.createdBy?.toString() === currentUserId?.toString();
            const hasQuotations = lead.quotations && lead.quotations.length > 0;
            const hasQuotationsSentToSales = lead.workflowStatus === 'forwarded_to_sales' ||
              (lead.quotations && lead.quotations.some(q => q.sentBy || q.status === 'sent'));

            return isCreatedByCurrentUser && hasQuotations && hasQuotationsSentToSales;
          });

          // Extract quotations
          const allQuotations = [];
          leadsWithQuotations.forEach(lead => {
            lead.quotations.forEach(quotation => {
              if (quotation.sentBy || quotation.status === 'sent' || quotation.status === 'accepted' || quotation.status === 'rejected') {
                allQuotations.push({
                  ...quotation,
                  leadId: lead._id,
                  customerName: lead.customerName,
                  customerEmail: lead.email,
                  destination: lead.destination,
                  lead: lead
                });
              }
            });
          });

          setQuotations(allQuotations);
          setLoadingQuotations(false);
        } catch (error) {
          console.error('Error fetching quotations:', error);
          setLoadingQuotations(false);
        }
      };

      doFetch();
      setQuotationsLoaded(true);
    }
  }, [activeTab, quotationsLoaded, user]);

  useEffect(() => {
    // Calculate analytics whenever data changes
    calculateAnalytics();
  }, [leads, forwardedLeads, quotations]);

  const calculateAnalytics = () => {
    const currentUserId = user?.id || user?._id;

    // Tour Booking Analytics with Workflow Breakdown
    const tourBookingLeads = leads.filter(l => l.leadType === 'tour_booking');
    const tourBookingForwarded = forwardedLeads.filter(l => l.leadType === 'tour_booking');

    // Tour Booking Workflow Status Breakdown
    const tourJustAdded = tourBookingLeads.filter(l =>
      !l.workflowStatus || l.workflowStatus === 'sales_new'
    ).length;

    const tourForwardedToReservation = [...tourBookingLeads, ...tourBookingForwarded].filter(l =>
      l.workflowStatus === 'forwarded_to_reservation' ||
      l.workflowStatus === 'reservation_working' ||
      l.workflowStatus === 'vendor_approval_pending' ||
      l.workflowStatus === 'vendor_approved' ||
      l.workflowStatus === 'quotation_prepared'
    ).length;

    const tourQuotationSent = quotations.filter(q =>
      q.status === 'sent' || q.sentToClient === true
    ).length;

    const tourQuotationAccepted = quotations.filter(q =>
      q.status === 'accepted'
    ).length;

    const tourConversionRate = tourBookingForwarded.length > 0
      ? ((tourBookingForwarded.length / tourBookingLeads.length) * 100).toFixed(1)
      : 0;

    // Quotations Analytics
    const acceptedQuotations = quotations.filter(q => q.status === 'accepted');
    const totalRevenue = acceptedQuotations.reduce((sum, q) => {
      const amount = q.items?.[0]?.totalPrice || q.packages?.[0]?.totalPrice || 0;
      return sum + amount;
    }, 0);
    const quotationsReceived = quotations.length;
    const quotationsAccepted = acceptedQuotations.length;
    const quotationsRejected = quotations.filter(q => q.status === 'rejected').length;
    const quotationsPending = quotations.filter(q => q.status === 'sent').length;
    const sentToClient = quotations.filter(q => q.sentToClient === true).length;

    // Other Services Analytics with Service Type Breakdown
    const otherServicesLeads = leads.filter(l => l.leadType === 'other_services');
    const otherServicesAssigned = otherServicesLeads.filter(l => l.assignedTo).length;
    const otherServicesCompleted = otherServicesLeads.filter(l => l.status === 'completed' || l.workflowStatus === 'completed').length;
    const otherServicesPending = otherServicesLeads.length - otherServicesCompleted;
    const otherServicesCompletionRate = otherServicesLeads.length > 0
      ? ((otherServicesCompleted / otherServicesLeads.length) * 100).toFixed(1)
      : 0;

    // Service Type Breakdown
    const serviceTypes = ['visa', 'passport', 'insurance', 'hotel_booking', 'flight_booking', 'other'];
    const serviceTypeBreakdown = {};

    serviceTypes.forEach(type => {
      const typeLeads = otherServicesLeads.filter(l => l.serviceType === type);
      serviceTypeBreakdown[type] = {
        total: typeLeads.length,
        new: typeLeads.filter(l => !l.assignedTo && l.status !== 'completed').length,
        assigned: typeLeads.filter(l => l.assignedTo && l.status !== 'completed').length,
        completed: typeLeads.filter(l => l.status === 'completed' || l.workflowStatus === 'completed').length
      };
    });

    // Overall Analytics
    const totalLeads = leads.length + forwardedLeads.length;
    const activeLeads = leads.length;
    const successfulLeads = otherServicesCompleted + quotationsAccepted;
    const successRate = totalLeads > 0
      ? ((successfulLeads / totalLeads) * 100).toFixed(1)
      : 0;

    setAnalytics({
      tourBooking: {
        total: tourBookingLeads.length,
        forwarded: tourBookingForwarded.length,
        conversionRate: tourConversionRate,
        quotationsReceived: quotationsReceived,
        quotationsAccepted: quotationsAccepted,
        quotationsRejected: quotationsRejected,
        quotationsPending: quotationsPending,
        sentToClient: sentToClient,
        totalRevenue: totalRevenue,
        // Workflow breakdown
        justAdded: tourJustAdded,
        forwardedToReservation: tourForwardedToReservation,
        quotationSent: tourQuotationSent,
        quotationAccepted: tourQuotationAccepted
      },
      otherServices: {
        total: otherServicesLeads.length,
        assigned: otherServicesAssigned,
        completed: otherServicesCompleted,
        pending: otherServicesPending,
        completionRate: otherServicesCompletionRate,
        // Service type breakdown
        byServiceType: serviceTypeBreakdown
      },
      overall: {
        totalLeads: totalLeads,
        activeLeads: activeLeads,
        responseTime: 0, // Can be calculated from lead timestamps
        successRate: successRate,
        totalRevenue: totalRevenue
      }
    });
  };

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      const response = await axios.get('/leads', {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('📊 Leads API response:', response.data);

      // Handle different response formats
      let allLeads = [];
      if (Array.isArray(response.data)) {
        allLeads = response.data;
      } else if (response.data.leads && Array.isArray(response.data.leads)) {
        allLeads = response.data.leads;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        allLeads = response.data.data;
      }

      // Get current user ID for filtering
      const currentUserId = user?.id || user?._id;
      console.log('👤 Current user ID:', currentUserId);

      // Filter leads for sales team - Active leads created by current user
      const salesLeads = allLeads.filter(lead => {
        // Check if lead is owned by sales team
        const isOwnedBySales = lead.currentOwner === 'sales' ||
          lead.currentOwner === 'other_services' || // Legacy support
          lead.workflowStatus === 'sales_new' ||
          !lead.currentOwner;

        // Check if lead was created by current user
        const isCreatedByCurrentUser = lead.createdBy === currentUserId ||
          lead.createdBy?.toString() === currentUserId?.toString();

        // Show only leads created by current user that are with sales team
        return isOwnedBySales && isCreatedByCurrentUser;
      });

      // Filter forwarded leads - Leads sent to reservation OR quotations sent back to sales (created by current user)
      const forwarded = allLeads.filter(lead => {
        const isCreatedByCurrentUser = lead.createdBy === currentUserId || lead.createdBy?.toString() === currentUserId?.toString();

        // Lead is with reservation team
        const isWithReservation = lead.currentOwner === 'reservation' &&
          (lead.workflowStatus === 'forwarded_to_reservation' ||
            lead.workflowStatus === 'reservation_working' ||
            lead.workflowStatus === 'vendor_approval_pending' ||
            lead.workflowStatus === 'vendor_approved' ||
            lead.workflowStatus === 'quotation_prepared');

        // Lead has quotations sent back to sales
        const hasQuotationsSentToSales = lead.workflowStatus === 'forwarded_to_sales' ||
          (lead.quotations && lead.quotations.some(q => q.sentBy || q.status === 'sent'));

        // Show forwarded leads created by current user (either with reservation or quotations sent back)
        return isCreatedByCurrentUser && (isWithReservation || hasQuotationsSentToSales);
      });

      console.log('✅ Sales leads:', salesLeads.length);
      console.log('📋 Forwarded leads:', forwarded.length);
      console.log('📋 Setting leads state:', salesLeads);
      setLeads(salesLeads);
      setForwardedLeads(forwarded);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLoading(false);
    }
  };



  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      const response = await axios.get('/employees', {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Store all employees for lookup
      const allEmployees = response.data || [];

      // Filter only Other Services team employees for assignment
      const otherServicesEmployees = allEmployees.filter(emp =>
        emp.teamType === 'other_services' && emp.isActive
      );

      setEmployees(otherServicesEmployees);
      console.log('👥 Other Services Employees:', otherServicesEmployees.length);

      // Store all employees in state for lookup
      localStorage.setItem('allEmployees', JSON.stringify(allEmployees));
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchQuotations = async () => {
    try {
      setLoadingQuotations(true);
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      const currentUserId = user?.id || user?._id;

      // Fetch leads that have been forwarded to sales with quotations
      const response = await axios.get('/leads', {
        headers: { Authorization: `Bearer ${token}` }
      });

      let allLeads = [];
      if (Array.isArray(response.data)) {
        allLeads = response.data;
      } else if (response.data.leads) {
        allLeads = response.data.leads;
      }

      console.log('🔍 Current User ID for quotations:', currentUserId);
      console.log('📋 Total leads fetched:', allLeads.length);

      // Filter leads that have quotations sent to sales AND were created by current user
      const leadsWithQuotations = allLeads.filter(lead => {
        const isCreatedByCurrentUser = lead.createdBy === currentUserId ||
          lead.createdBy?.toString() === currentUserId?.toString();
        const hasQuotations = lead.quotations && lead.quotations.length > 0;
        const hasQuotationsSentToSales = lead.workflowStatus === 'forwarded_to_sales' ||
          (lead.quotations && lead.quotations.some(q => q.sentBy || q.status === 'sent'));

        // Debug logging
        if (hasQuotations && hasQuotationsSentToSales && !isCreatedByCurrentUser) {
          console.log('⚠️ Quotation found but not created by current user:', {
            customerName: lead.customerName,
            createdBy: lead.createdBy,
            currentUserId: currentUserId,
            workflowStatus: lead.workflowStatus,
            quotationsCount: lead.quotations.length
          });
        }

        return isCreatedByCurrentUser && hasQuotations && hasQuotationsSentToSales;
      });

      console.log('📊 Leads with quotations for sales:', leadsWithQuotations.length);
      if (leadsWithQuotations.length > 0) {
        console.log('📄 Sample quotation lead:', {
          customerName: leadsWithQuotations[0].customerName,
          quotationsCount: leadsWithQuotations[0].quotations.length,
          workflowStatus: leadsWithQuotations[0].workflowStatus
        });
      }

      // Extract quotations with lead info
      const allQuotations = [];
      leadsWithQuotations.forEach(lead => {
        lead.quotations.forEach(quotation => {
          // Show all quotations that have been sent to sales
          if (quotation.sentBy || quotation.status === 'sent' || quotation.status === 'accepted' || quotation.status === 'rejected') {
            allQuotations.push({
              ...quotation,
              leadId: lead._id,
              customerName: lead.customerName,
              customerEmail: lead.email,
              destination: lead.destination,
              lead: lead
            });
          }
        });
      });

      console.log('✅ Total quotations for sales:', allQuotations.length);

      setQuotations(allQuotations);
      setLoadingQuotations(false);
    } catch (error) {
      console.error('Error fetching quotations:', error);
      setLoadingQuotations(false);
    }
  };

  const getEmployeeName = (employeeId) => {
    if (!employeeId) {
      console.log('⚠️ No employee ID provided');
      return 'Not Assigned';
    }

    console.log('🔍 Looking for employee:', employeeId);

    // Try to get from employees state first
    const employee = employees.find(emp =>
      emp._id === employeeId ||
      emp.id === employeeId ||
      emp._id?.toString() === employeeId?.toString()
    );

    if (employee) {
      console.log('✅ Found employee in state:', employee.teamMemberName);
      return employee.teamMemberName || employee.name || employee.email;
    }

    // Try from localStorage
    try {
      const allEmployees = JSON.parse(localStorage.getItem('allEmployees') || '[]');
      const emp = allEmployees.find(e =>
        e._id === employeeId ||
        e.id === employeeId ||
        e._id?.toString() === employeeId?.toString()
      );
      if (emp) {
        console.log('✅ Found employee in localStorage:', emp.teamMemberName);
        return emp.teamMemberName || emp.name || emp.email;
      }
    } catch (e) {
      console.error('❌ Error parsing employees:', e);
    }

    console.log('⚠️ Employee not found, returning ID:', employeeId);
    return `Employee (${employeeId.substring(0, 8)}...)`;
  };

  const assignLeadToEmployee = async () => {
    if (!selectedEmployee || !selectedLeadForAssign) {
      alert('Please select an employee');
      return;
    }

    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      // Get current user info
      const currentUser = user?.name || user?.email || 'Sales Team';

      await axios.patch(`/leads/${selectedLeadForAssign._id}`, {
        assignedTo: selectedEmployee,
        assignedEmployee: selectedEmployee,
        assignedAt: new Date(),
        assignedByName: currentUser
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Get employee name
      const employeeName = getEmployeeName(selectedEmployee);

      // Create localStorage notification for real-time popup
      assignmentNotificationService.createNotification(
        selectedLeadForAssign,
        selectedEmployee,
        employeeName
      );
      console.log('✅ LocalStorage notification created for employee:', employeeName);

      // Create database notification for the assigned employee
      try {
        await axios.post('/notifications/create', {
          type: 'lead_assigned',
          leadId: selectedLeadForAssign._id,
          recipientId: selectedEmployee,
          recipientType: 'employee',
          title: 'New Service Lead Assigned!',
          message: `New ${selectedLeadForAssign.serviceType?.replace(/_/g, ' ')} lead from ${selectedLeadForAssign.customerName}`,
          metadata: {
            customerName: selectedLeadForAssign.customerName,
            serviceType: selectedLeadForAssign.serviceType,
            email: selectedLeadForAssign.email,
            contactNumber: selectedLeadForAssign.contactNumber,
            assignedByName: currentUser
          }
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('✅ Database notification created');
      } catch (notifError) {
        console.error('Error creating database notification:', notifError);
      }

      alert(`✅ Lead assigned successfully to ${employeeName}!`);
      setShowAssignModal(false);
      setSelectedLeadForAssign(null);
      setSelectedEmployee('');
      fetchLeads();
    } catch (error) {
      console.error('Error assigning lead:', error);
      alert('Error assigning lead');
    }
  };

  const forwardToReservation = async (leadId) => {
    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      await axios.post(`/leads/${leadId}/forward-to-reservation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLeads();
      alert('Lead forwarded to Reservation Team');
    } catch (error) {
      console.error('Error forwarding lead:', error);
      alert('Error forwarding lead');
    }
  };



  const handleDeleteLead = async (leadId, customerName) => {
    if (!window.confirm(`Are you sure you want to delete lead for ${customerName}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      await axios.delete(`/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Lead deleted successfully');
      fetchLeads();
    } catch (error) {
      console.error('Error deleting lead:', error);

      // If 404, lead was already deleted - just refresh the list
      if (error.response?.status === 404) {
        alert('This lead was already deleted or no longer exists. Refreshing the list...');
        fetchLeads(); // Refresh to remove stale data
      } else {
        alert('Error deleting lead: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleAcceptQuotation = async (quotation) => {
    const amount = quotation.items?.[0]?.totalPrice || quotation.packages?.[0]?.totalPrice || 0;
    if (!window.confirm(`Accept this quotation for ₹${amount.toLocaleString()}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      await axios.post(`/leads/${quotation.leadId}/accept-quotation`, {
        quotationId: quotation._id,
        acceptedBy: user?.id,
        acceptedByName: user?.name || user?.email || 'Sales Team'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ Quotation accepted successfully!');
      refreshQuotations();
    } catch (error) {
      console.error('Error accepting quotation:', error);
      alert('Error accepting quotation: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleRejectQuotation = (quotation) => {
    setSelectedQuotationForReject(quotation);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const submitRejectQuotation = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      await axios.post(`/leads/${selectedQuotationForReject.leadId}/reject-quotation`, {
        quotationId: selectedQuotationForReject._id,
        rejectedBy: user?.id,
        rejectedByName: user?.name || user?.email || 'Sales Team',
        rejectionReason: rejectReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Quotation rejected');
      setShowRejectModal(false);
      setSelectedQuotationForReject(null);
      setRejectReason('');
      refreshQuotations();
    } catch (error) {
      console.error('Error rejecting quotation:', error);
      alert('Error rejecting quotation: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteQuotation = async (quotation) => {
    if (!window.confirm(`Are you sure you want to delete this quotation for ${quotation.customerName}?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      await axios.delete(`/leads/${quotation.leadId}/quotations/${quotation._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ Quotation deleted successfully');
      refreshQuotations();
    } catch (error) {
      console.error('Error deleting quotation:', error);
      alert('Error deleting quotation: ' + (error.response?.data?.message || error.message));
    }
  };

  console.log('🔍 SalesDashboard render - Leads count:', leads.length);

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Quotation Notification Popup */}
      <NewLeadNotificationPopup
        notificationType="quotation_forwarded_to_sales"
        onNewLeadDetected={(quotationIds) => {
          console.log('🔔 New quotations detected, refreshing...', quotationIds);
          refreshQuotations();
          setUnreadNotificationCount(quotationIds.length);
        }}
        onViewLead={(lead) => {
          refreshQuotations();
          setActiveTab('quotations');
          setUnreadNotificationCount(0);
        }}
      />

      {/* Professional Sidebar */}
      <div className="w-56 bg-gradient-to-b from-indigo-900 to-indigo-800 text-white shadow-2xl flex flex-col fixed h-screen z-40 left-0 top-0">
        {/* Header */}
        <div className="p-4 border-b border-indigo-700">
          <h2 className="text-lg font-bold">Sales</h2>
          <p className="text-xs text-indigo-200 mt-0.5">Dashboard</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1.5 overflow-y-auto">
          <button
            onClick={() => setActiveTab('analytics')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${activeTab === 'analytics'
              ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white shadow-lg font-semibold'
              : 'text-indigo-100 hover:bg-indigo-700 hover:text-white'
              }`}
          >
            <span className="text-sm">📊</span>
            <div className="flex-1">
              <div className="text-sm font-medium">Analytics Dashboard</div>
              <div className="text-xs opacity-75">Performance metrics</div>
            </div>
          </button>

          <div className="border-t border-indigo-700 my-2"></div>

          <button
            onClick={() => setActiveTab('tour_booking')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${activeTab === 'tour_booking'
              ? 'bg-white text-indigo-900 shadow-lg font-semibold'
              : 'text-indigo-100 hover:bg-indigo-700 hover:text-white'
              }`}
          >
            <span className="text-sm">🎯</span>
            <div className="flex-1">
              <div className="text-sm font-medium">Tour Booking</div>
              <div className="text-xs opacity-75">{leads.filter(l => l.leadType === 'tour_booking').length} leads</div>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('other_services')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${activeTab === 'other_services'
              ? 'bg-white text-teal-600 shadow-lg font-semibold'
              : 'text-indigo-100 hover:bg-indigo-700 hover:text-white'
              }`}
          >
            <span className="text-sm">📋</span>
            <div className="flex-1">
              <div className="text-sm font-medium">Other Services</div>
              <div className="text-xs opacity-75">{leads.filter(l => l.leadType === 'other_services').length} leads</div>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('forwarded')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${activeTab === 'forwarded'
              ? 'bg-white text-purple-600 shadow-lg font-semibold'
              : 'text-indigo-100 hover:bg-indigo-700 hover:text-white'
              }`}
          >
            <span className="text-sm">📤</span>
            <div className="flex-1">
              <div className="text-sm font-medium">Forwarded</div>
              <div className="text-xs opacity-75">{forwardedLeads.length} with reservation</div>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('quotations')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 relative ${activeTab === 'quotations'
              ? 'bg-white text-blue-600 shadow-lg font-semibold'
              : 'text-indigo-100 hover:bg-indigo-700 hover:text-white'
              }`}
          >
            <span className="text-sm">📄</span>
            <div className="flex-1">
              <div className="text-sm font-medium">Quotations</div>
              <div className="text-xs opacity-75">{quotations.length} received</div>
            </div>
            {unreadNotificationCount > 0 && (
              <span className="absolute top-1 right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                {unreadNotificationCount}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('followUps')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${activeTab === 'followUps'
              ? 'bg-white text-orange-600 shadow-lg font-semibold'
              : 'text-indigo-100 hover:bg-indigo-700 hover:text-white'
              }`}
          >
            <span className="text-sm">📅</span>
            <div className="flex-1">
              <div className="text-sm font-medium">Follow-ups</div>
              <div className="text-xs opacity-75">Manage tasks</div>
            </div>
          </button>

          {/* Status Tracker - Cancelled & On-Hold */}
          <button
            onClick={() => setActiveTab('status_tracker')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${activeTab === 'status_tracker'
              ? 'bg-white text-red-600 shadow-lg font-semibold'
              : 'text-indigo-100 hover:bg-indigo-700 hover:text-white'
              }`}
          >
            <span className="text-sm">🚨</span>
            <div className="flex-1">
              <div className="text-sm font-medium">Status Tracker</div>
              <div className="text-xs opacity-75">
                {leads.filter(l => l.workflowStatus === 'cancelled' || l.workflowStatus === 'on_hold').length} leads
              </div>
            </div>
            {leads.filter(l => l.workflowStatus === 'cancelled' || l.workflowStatus === 'on_hold').length > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {leads.filter(l => l.workflowStatus === 'cancelled' || l.workflowStatus === 'on_hold').length}
              </span>
            )}
          </button>
        </nav>

        {/* Quick Actions */}
        <div className="p-3 border-t border-indigo-700 bg-indigo-950">
          <button
            onClick={() => {
              console.log('🔵 Add Lead button clicked!');
              setShowAddLead(true);
            }}
            className="w-full px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg font-semibold flex items-center justify-center gap-2 text-xs"
          >
            <span>➕</span>
            <span>Add New Lead</span>
          </button>
        </div>

        {/* User Info & Logout */}
        <div className="p-3 border-t border-indigo-700 bg-indigo-950">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold">{user?.teamMemberName?.charAt(0) || user?.name?.charAt(0) || 'S'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold truncate">{user?.teamMemberName || user?.name || 'Sales User'}</p>
              <p className="text-xs text-indigo-300 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={() => {
              localStorage.clear();
              window.location.href = '/login';
            }}
            className="w-full px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all text-xs font-semibold flex items-center justify-center gap-2"
          >
            <span>🚪</span>
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="ml-56 min-h-screen bg-gray-50">
        <div className="p-4">
          <div className="max-w-7xl mx-auto">

            {/* Header with Add Button */}
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Sales Dashboard</h1>
                <p className="text-gray-600 text-xs mt-0.5">Manage your leads and quotations</p>
              </div>
              <button
                onClick={() => setShowAddLead(true)}
                className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md font-semibold flex items-center gap-1.5 text-xs"
              >
                <span>➕</span>
                <span>Add New Lead</span>
              </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
              <div className="bg-white p-2 rounded-lg shadow-sm hover:shadow transition-shadow border-l-4 border-blue-500">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-gray-600 text-xs font-medium">Tour Booking</span>
                  <span className="text-xs">🎯</span>
                </div>
                <p className="text-lg font-bold text-gray-900">{leads.filter(l => l.leadType === 'tour_booking').length}</p>
              </div>

              <div className="bg-white p-2 rounded-lg shadow-sm hover:shadow transition-shadow border-l-4 border-teal-500">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-gray-600 text-xs font-medium">Other Services</span>
                  <span className="text-xs">📋</span>
                </div>
                <p className="text-lg font-bold text-gray-900">
                  {leads.filter(l => l.leadType === 'other_services').length}
                </p>
              </div>

              <div className="bg-white p-2 rounded-lg shadow-sm hover:shadow transition-shadow border-l-4 border-green-500">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-gray-600 text-xs font-medium">Assigned</span>
                  <span className="text-xs">✅</span>
                </div>
                <p className="text-lg font-bold text-green-700">
                  {leads.filter(l => l.leadType === 'other_services' && (l.assignedTo || l.assignedEmployee)).length}
                </p>
              </div>

              <div className="bg-white p-2 rounded-lg shadow-sm hover:shadow transition-shadow border-l-4 border-yellow-500">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-gray-600 text-xs font-medium">Pending</span>
                  <span className="text-xs">⏳</span>
                </div>
                <p className="text-lg font-bold text-yellow-700">
                  {leads.filter(l => l.leadType === 'other_services' && !l.assignedTo && !l.assignedEmployee).length}
                </p>
              </div>

              <div className="bg-white p-2 rounded-lg shadow-sm hover:shadow transition-shadow border-l-4 border-purple-500">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-purple-600 text-xs font-medium">Reservation</span>
                  <span className="text-xs">📤</span>
                </div>
                <p className="text-lg font-bold text-purple-700">{forwardedLeads.length}</p>
              </div>
            </div>

            {/* Analytics Dashboard */}
            {activeTab === 'analytics' && (
              <div className="space-y-3">
                {/* Compact Header */}
                <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-lg px-4 py-3 text-white shadow-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">📊</span>
                      <div>
                        <h2 className="text-lg font-bold">Sales Analytics</h2>
                        <p className="text-indigo-100 text-xs">Performance metrics overview</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{analytics.overall.totalLeads}</p>
                      <p className="text-xs opacity-80">Total Leads</p>
                    </div>
                  </div>
                </div>

                {/* Compact KPI Cards - 5 in a row */}
                <div className="grid grid-cols-5 gap-2">
                  <div className="bg-white rounded-lg p-3 shadow-sm border border-blue-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center text-sm">📈</span>
                      <span className="text-xs text-gray-500 font-medium">Total</span>
                    </div>
                    <p className="text-xl font-bold text-blue-600">{analytics.overall.totalLeads}</p>
                  </div>

                  <div className="bg-white rounded-lg p-3 shadow-sm border border-green-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center text-sm">⚡</span>
                      <span className="text-xs text-gray-500 font-medium">Active</span>
                    </div>
                    <p className="text-xl font-bold text-green-600">{analytics.overall.activeLeads}</p>
                  </div>

                  <div className="bg-white rounded-lg p-3 shadow-sm border border-yellow-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-7 h-7 bg-yellow-100 rounded-full flex items-center justify-center text-sm">💰</span>
                      <span className="text-xs text-gray-500 font-medium">Revenue</span>
                    </div>
                    <p className="text-xl font-bold text-yellow-600">₹{((analytics.overall.totalRevenue || 0) / 100000).toFixed(1)}L</p>
                  </div>

                  <div className="bg-white rounded-lg p-3 shadow-sm border border-purple-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center text-sm">🎯</span>
                      <span className="text-xs text-gray-500 font-medium">Success</span>
                    </div>
                    <p className="text-xl font-bold text-purple-600">{analytics.overall.successRate}%</p>
                  </div>

                  <div className="bg-white rounded-lg p-3 shadow-sm border border-orange-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-7 h-7 bg-orange-100 rounded-full flex items-center justify-center text-sm">✅</span>
                      <span className="text-xs text-gray-500 font-medium">Accepted</span>
                    </div>
                    <p className="text-xl font-bold text-orange-600">{analytics.tourBooking.quotationsAccepted || 0}</p>
                  </div>
                </div>

                {/* Tour Booking Analytics - Compact */}
                <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white text-lg">🎯</span>
                      <h3 className="text-sm font-bold text-gray-800">Tour Booking Pipeline</h3>
                    </div>
                    <span className="text-xs text-gray-500">{analytics.tourBooking.conversionRate}% conversion</span>
                  </div>

                  <div className="grid grid-cols-6 gap-2 mb-3">
                    <div className="bg-blue-50 rounded p-2 border-l-3 border-blue-500">
                      <p className="text-xs text-gray-500">Created</p>
                      <p className="text-lg font-bold text-blue-600">{analytics.tourBooking.total}</p>
                    </div>
                    <div className="bg-purple-50 rounded p-2 border-l-3 border-purple-500">
                      <p className="text-xs text-gray-500">Forwarded</p>
                      <p className="text-lg font-bold text-purple-600">{analytics.tourBooking.forwarded}</p>
                    </div>
                    <div className="bg-indigo-50 rounded p-2 border-l-3 border-indigo-500">
                      <p className="text-xs text-gray-500">Quotations</p>
                      <p className="text-lg font-bold text-indigo-600">{analytics.tourBooking.quotationsReceived || 0}</p>
                    </div>
                    <div className="bg-green-50 rounded p-2 border-l-3 border-green-500">
                      <p className="text-xs text-gray-500">Accepted</p>
                      <p className="text-lg font-bold text-green-600">{analytics.tourBooking.quotationsAccepted || 0}</p>
                    </div>
                    <div className="bg-yellow-50 rounded p-2 border-l-3 border-yellow-500">
                      <p className="text-xs text-gray-500">Revenue</p>
                      <p className="text-lg font-bold text-yellow-600">₹{((analytics.tourBooking.totalRevenue || 0) / 100000).toFixed(1)}L</p>
                    </div>
                    <div className="bg-orange-50 rounded p-2 border-l-3 border-orange-500">
                      <p className="text-xs text-gray-500">Rate</p>
                      <p className="text-lg font-bold text-orange-600">{analytics.tourBooking.conversionRate}%</p>
                    </div>
                  </div>

                  {/* Compact Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${analytics.tourBooking.conversionRate}%` }}
                    ></div>
                  </div>
                </div>

                {/* Tour Bookings Workflow Status - Compact */}
                <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center text-white text-lg">🔄</span>
                      <h3 className="text-sm font-bold text-gray-800">Workflow Status</h3>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-500">
                        {analytics.tourBooking.quotationAccepted || 0}/{analytics.tourBooking.total} completed
                      </span>
                      <span className="text-lg font-bold text-purple-600">{analytics.tourBooking.total}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {/* Just Added */}
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-lg">🆕</span>
                        <span className="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded">{analytics.tourBooking.justAdded || 0}</span>
                      </div>
                      <p className="text-xs font-medium text-gray-700">Just Added</p>
                    </div>

                    {/* With Reservation */}
                    <div className="bg-purple-50 rounded-lg p-3 border border-purple-200 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-lg">📤</span>
                        <span className="bg-purple-500 text-white text-xs font-bold px-2 py-0.5 rounded">{analytics.tourBooking.forwardedToReservation || 0}</span>
                      </div>
                      <p className="text-xs font-medium text-gray-700">With Reservation</p>
                    </div>

                    {/* Sent to Client */}
                    <div className="bg-green-50 rounded-lg p-3 border border-green-200 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-lg">📧</span>
                        <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded">{analytics.tourBooking.quotationSent || 0}</span>
                      </div>
                      <p className="text-xs font-medium text-gray-700">Sent to Client</p>
                    </div>

                    {/* Accepted */}
                    <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-lg">✅</span>
                        <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded">{analytics.tourBooking.quotationAccepted || 0}</span>
                      </div>
                      <p className="text-xs font-medium text-gray-700">Accepted</p>
                    </div>
                  </div>

                  {/* Compact Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${analytics.tourBooking.total > 0 ? ((analytics.tourBooking.quotationAccepted || 0) / analytics.tourBooking.total * 100) : 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Other Services Analytics - Compact */}
                <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center text-white text-lg">📋</span>
                      <h3 className="text-sm font-bold text-gray-800">Other Services</h3>
                    </div>
                    <span className="text-xs text-gray-500">{analytics.otherServices.completionRate}% completion</span>
                  </div>

                  <div className="grid grid-cols-5 gap-2 mb-3">
                    <div className="bg-teal-50 rounded p-2 border-l-3 border-teal-500">
                      <p className="text-xs text-gray-500">Total</p>
                      <p className="text-lg font-bold text-teal-600">{analytics.otherServices.total}</p>
                    </div>
                    <div className="bg-blue-50 rounded p-2 border-l-3 border-blue-500">
                      <p className="text-xs text-gray-500">Assigned</p>
                      <p className="text-lg font-bold text-blue-600">{analytics.otherServices.assigned}</p>
                    </div>
                    <div className="bg-green-50 rounded p-2 border-l-3 border-green-500">
                      <p className="text-xs text-gray-500">Completed</p>
                      <p className="text-lg font-bold text-green-600">{analytics.otherServices.completed}</p>
                    </div>
                    <div className="bg-yellow-50 rounded p-2 border-l-3 border-yellow-500">
                      <p className="text-xs text-gray-500">Pending</p>
                      <p className="text-lg font-bold text-yellow-600">{analytics.otherServices.pending}</p>
                    </div>
                    <div className="bg-green-50 rounded p-2 border-l-3 border-green-500">
                      <p className="text-xs text-gray-500">Rate</p>
                      <p className="text-lg font-bold text-green-600">{analytics.otherServices.completionRate}%</p>
                    </div>
                  </div>

                  {/* Compact Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-teal-500 to-green-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${analytics.otherServices.completionRate}%` }}
                    ></div>
                  </div>
                </div>

                {/* Service Type Breakdown - Compact */}
                <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-lg flex items-center justify-center text-white text-lg">🔍</span>
                      <h3 className="text-sm font-bold text-gray-800">Service Type Breakdown</h3>
                    </div>
                    <span className="text-lg font-bold text-teal-600">{analytics.otherServices.total}</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {/* Visa */}
                    <button
                      onClick={() => {
                        setServiceTypeFilter('visa');
                        setActiveTab('other_services');
                      }}
                      className="group relative bg-gradient-to-br from-blue-50 via-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 rounded-xl p-5 border-2 border-blue-300 hover:border-blue-500 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 text-left"
                    >
                      <div className="absolute top-0 right-0 w-16 h-16 bg-blue-300 rounded-full opacity-20 -mr-8 -mt-8"></div>
                      <div className="relative">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-3xl group-hover:scale-110 transition-transform duration-300">🛂</span>
                          <div className="bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-md">
                            {analytics.otherServices.byServiceType?.visa?.total || 0}
                          </div>
                        </div>
                        <p className="text-sm font-bold text-gray-800 mb-2">Visa Services</p>
                        <div className="flex gap-3 text-xs">
                          <span className="flex items-center gap-1 text-gray-600">
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                            {analytics.otherServices.byServiceType?.visa?.new || 0} New
                          </span>
                          <span className="flex items-center gap-1 text-green-600 font-semibold">
                            ✓ {analytics.otherServices.byServiceType?.visa?.completed || 0}
                          </span>
                        </div>
                      </div>
                    </button>

                    {/* Passport */}
                    <button
                      onClick={() => {
                        setServiceTypeFilter('passport');
                        setActiveTab('other_services');
                      }}
                      className="group relative bg-gradient-to-br from-purple-50 via-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 rounded-xl p-5 border-2 border-purple-300 hover:border-purple-500 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 text-left"
                    >
                      <div className="absolute top-0 right-0 w-16 h-16 bg-purple-300 rounded-full opacity-20 -mr-8 -mt-8"></div>
                      <div className="relative">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-3xl group-hover:scale-110 transition-transform duration-300">📘</span>
                          <div className="bg-purple-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-md">
                            {analytics.otherServices.byServiceType?.passport?.total || 0}
                          </div>
                        </div>
                        <p className="text-sm font-bold text-gray-800 mb-2">Passport</p>
                        <div className="flex gap-3 text-xs">
                          <span className="flex items-center gap-1 text-gray-600">
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                            {analytics.otherServices.byServiceType?.passport?.new || 0} New
                          </span>
                          <span className="flex items-center gap-1 text-green-600 font-semibold">
                            ✓ {analytics.otherServices.byServiceType?.passport?.completed || 0}
                          </span>
                        </div>
                      </div>
                    </button>

                    {/* Insurance */}
                    <button
                      onClick={() => {
                        setServiceTypeFilter('insurance');
                        setActiveTab('other_services');
                      }}
                      className="group relative bg-gradient-to-br from-green-50 via-green-50 to-green-100 hover:from-green-100 hover:to-green-200 rounded-xl p-5 border-2 border-green-300 hover:border-green-500 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 text-left"
                    >
                      <div className="absolute top-0 right-0 w-16 h-16 bg-green-300 rounded-full opacity-20 -mr-8 -mt-8"></div>
                      <div className="relative">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-3xl group-hover:scale-110 transition-transform duration-300">🛡️</span>
                          <div className="bg-green-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-md">
                            {analytics.otherServices.byServiceType?.insurance?.total || 0}
                          </div>
                        </div>
                        <p className="text-sm font-bold text-gray-800 mb-2">Insurance</p>
                        <div className="flex gap-3 text-xs">
                          <span className="flex items-center gap-1 text-gray-600">
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                            {analytics.otherServices.byServiceType?.insurance?.new || 0} New
                          </span>
                          <span className="flex items-center gap-1 text-green-600 font-semibold">
                            ✓ {analytics.otherServices.byServiceType?.insurance?.completed || 0}
                          </span>
                        </div>
                      </div>
                    </button>

                    {/* Hotel Booking */}
                    <button
                      onClick={() => {
                        setServiceTypeFilter('hotel_booking');
                        setActiveTab('other_services');
                      }}
                      className="group relative bg-gradient-to-br from-orange-50 via-orange-50 to-orange-100 hover:from-orange-100 hover:to-orange-200 rounded-xl p-5 border-2 border-orange-300 hover:border-orange-500 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 text-left"
                    >
                      <div className="absolute top-0 right-0 w-16 h-16 bg-orange-300 rounded-full opacity-20 -mr-8 -mt-8"></div>
                      <div className="relative">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-3xl group-hover:scale-110 transition-transform duration-300">🏨</span>
                          <div className="bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-md">
                            {analytics.otherServices.byServiceType?.hotel_booking?.total || 0}
                          </div>
                        </div>
                        <p className="text-sm font-bold text-gray-800 mb-2">Hotel Booking</p>
                        <div className="flex gap-3 text-xs">
                          <span className="flex items-center gap-1 text-gray-600">
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                            {analytics.otherServices.byServiceType?.hotel_booking?.new || 0} New
                          </span>
                          <span className="flex items-center gap-1 text-green-600 font-semibold">
                            ✓ {analytics.otherServices.byServiceType?.hotel_booking?.completed || 0}
                          </span>
                        </div>
                      </div>
                    </button>

                    {/* Flight Booking */}
                    <button
                      onClick={() => {
                        setServiceTypeFilter('flight_booking');
                        setActiveTab('other_services');
                      }}
                      className="group relative bg-gradient-to-br from-indigo-50 via-indigo-50 to-indigo-100 hover:from-indigo-100 hover:to-indigo-200 rounded-xl p-5 border-2 border-indigo-300 hover:border-indigo-500 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 text-left"
                    >
                      <div className="absolute top-0 right-0 w-16 h-16 bg-indigo-300 rounded-full opacity-20 -mr-8 -mt-8"></div>
                      <div className="relative">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-3xl group-hover:scale-110 transition-transform duration-300">✈️</span>
                          <div className="bg-indigo-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-md">
                            {analytics.otherServices.byServiceType?.flight_booking?.total || 0}
                          </div>
                        </div>
                        <p className="text-sm font-bold text-gray-800 mb-2">Flight Booking</p>
                        <div className="flex gap-3 text-xs">
                          <span className="flex items-center gap-1 text-gray-600">
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                            {analytics.otherServices.byServiceType?.flight_booking?.new || 0} New
                          </span>
                          <span className="flex items-center gap-1 text-green-600 font-semibold">
                            ✓ {analytics.otherServices.byServiceType?.flight_booking?.completed || 0}
                          </span>
                        </div>
                      </div>
                    </button>

                    {/* Other */}
                    <button
                      onClick={() => {
                        setServiceTypeFilter('other');
                        setActiveTab('other_services');
                      }}
                      className="group relative bg-gradient-to-br from-gray-50 via-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 rounded-xl p-5 border-2 border-gray-300 hover:border-gray-500 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 text-left"
                    >
                      <div className="absolute top-0 right-0 w-16 h-16 bg-gray-300 rounded-full opacity-20 -mr-8 -mt-8"></div>
                      <div className="relative">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-3xl group-hover:scale-110 transition-transform duration-300">📦</span>
                          <div className="bg-gray-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-md">
                            {analytics.otherServices.byServiceType?.other?.total || 0}
                          </div>
                        </div>
                        <p className="text-sm font-bold text-gray-800 mb-2">Other Services</p>
                        <div className="flex gap-3 text-xs">
                          <span className="flex items-center gap-1 text-gray-600">
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                            {analytics.otherServices.byServiceType?.other?.new || 0} New
                          </span>
                          <span className="flex items-center gap-1 text-green-600 font-semibold">
                            ✓ {analytics.otherServices.byServiceType?.other?.completed || 0}
                          </span>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Quick Actions - Compact */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-3 border border-indigo-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-700">🚀 Quick Actions</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setActiveTab('tour_booking')}
                        className="flex items-center gap-2 bg-white hover:bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-left transition-all hover:shadow-sm text-sm"
                      >
                        <span>🎯</span>
                        <span className="font-medium text-gray-700">Tour Bookings ({analytics.tourBooking.total})</span>
                      </button>
                      <button
                        onClick={() => setActiveTab('other_services')}
                        className="flex items-center gap-2 bg-white hover:bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 text-left transition-all hover:shadow-sm text-sm"
                      >
                        <span>📋</span>
                        <span className="font-medium text-gray-700">Other Services ({analytics.otherServices.total})</span>
                      </button>
                      <button
                        onClick={() => setActiveTab('quotations')}
                        className="flex items-center gap-2 bg-white hover:bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-left transition-all hover:shadow-sm text-sm"
                      >
                        <span>📄</span>
                        <span className="font-medium text-gray-700">Quotations ({analytics.tourBooking.quotationsReceived || 0})</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Performance Insights - Compact */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
                    <h3 className="text-xs font-bold text-gray-700 mb-2">💡 Performance Insights</h3>
                    <div className="space-y-2">
                      {analytics.tourBooking.conversionRate >= 50 ? (
                        <div className="flex items-center gap-2 p-2 bg-green-50 rounded text-xs">
                          <span>🎉</span>
                          <span className="text-green-700">Excellent! {analytics.tourBooking.conversionRate}% conversion</span>
                        </div>
                      ) : analytics.tourBooking.conversionRate >= 30 ? (
                        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded text-xs">
                          <span>👍</span>
                          <span className="text-blue-700">Good! {analytics.tourBooking.conversionRate}% conversion</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 p-2 bg-yellow-50 rounded text-xs">
                          <span>💪</span>
                          <span className="text-yellow-700">Focus on follow-ups to improve</span>
                        </div>
                      )}

                      {analytics.otherServices.completionRate >= 70 ? (
                        <div className="flex items-center gap-2 p-2 bg-green-50 rounded text-xs">
                          <span>⭐</span>
                          <span className="text-green-700">{analytics.otherServices.completionRate}% service completion</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 p-2 bg-orange-50 rounded text-xs">
                          <span>🔔</span>
                          <span className="text-orange-700">{analytics.otherServices.pending} pending services</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
                    <h3 className="text-xs font-bold text-gray-700 mb-2">📈 Growth Metrics</h3>
                    <div className="space-y-2">
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-500">Tour Pipeline</span>
                          <span className="font-medium text-indigo-600">{analytics.tourBooking.forwarded} fwd</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div className="bg-indigo-600 h-1.5 rounded-full" style={{ width: `${analytics.tourBooking.total > 0 ? (analytics.tourBooking.forwarded / analytics.tourBooking.total * 100) : 0}%` }}></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-500">Quotation Success</span>
                          <span className="font-medium text-green-600">{analytics.tourBooking.sentToClient} sent</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div className="bg-green-600 h-1.5 rounded-full" style={{ width: `${analytics.tourBooking.quotationsReceived > 0 ? (analytics.tourBooking.sentToClient / analytics.tourBooking.quotationsReceived * 100) : 0}%` }}></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-500">Services</span>
                          <span className="font-medium text-teal-600">{analytics.otherServices.assigned} assigned</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div className="bg-teal-600 h-1.5 rounded-full" style={{ width: `${analytics.otherServices.total > 0 ? (analytics.otherServices.assigned / analytics.otherServices.total * 100) : 0}%` }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Professional Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Sales Funnel Bar Chart */}
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">📊 Sales Funnel Analysis</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={[
                          { stage: 'Created', count: analytics.tourBooking.total, fill: '#3b82f6' },
                          { stage: 'Forwarded', count: analytics.tourBooking.forwarded, fill: '#8b5cf6' },
                          { stage: 'Quotations', count: analytics.tourBooking.quotationsReceived, fill: '#10b981' },
                          { stage: 'Sent', count: analytics.tourBooking.sentToClient, fill: '#14b8a6' }
                        ]}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="stage" stroke="#6b7280" style={{ fontSize: '12px' }} />
                        <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                          }}
                        />
                        <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                          {[
                            { stage: 'Created', count: analytics.tourBooking.total, fill: '#3b82f6' },
                            { stage: 'Forwarded', count: analytics.tourBooking.forwarded, fill: '#8b5cf6' },
                            { stage: 'Quotations', count: analytics.tourBooking.quotationsReceived, fill: '#10b981' },
                            { stage: 'Sent', count: analytics.tourBooking.sentToClient, fill: '#14b8a6' }
                          ].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <p className="text-xs text-gray-500 text-center mt-2">Tour booking leads progression through sales funnel</p>
                  </div>

                  {/* Lead Distribution Pie Chart */}
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">🎯 Lead Distribution</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Tour Booking', value: analytics.tourBooking.total, color: '#3b82f6' },
                            { name: 'Other Services', value: analytics.otherServices.total, color: '#14b8a6' },
                            { name: 'Forwarded', value: analytics.tourBooking.forwarded, color: '#8b5cf6' }
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {[
                            { name: 'Tour Booking', value: analytics.tourBooking.total, color: '#3b82f6' },
                            { name: 'Other Services', value: analytics.otherServices.total, color: '#14b8a6' },
                            { name: 'Forwarded', value: analytics.tourBooking.forwarded, color: '#8b5cf6' }
                          ].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                          }}
                        />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          iconType="circle"
                          wrapperStyle={{ fontSize: '12px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <p className="text-xs text-gray-500 text-center mt-2">Distribution of leads across categories</p>
                  </div>

                  {/* Conversion Rate Line Chart */}
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">📈 Performance Trend</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart
                        data={[
                          { stage: 'Start', rate: 100 },
                          { stage: 'Forwarded', rate: analytics.tourBooking.total > 0 ? (analytics.tourBooking.forwarded / analytics.tourBooking.total * 100) : 0 },
                          { stage: 'Quotations', rate: analytics.tourBooking.forwarded > 0 ? (analytics.tourBooking.quotationsReceived / analytics.tourBooking.forwarded * 100) : 0 },
                          { stage: 'Conversion', rate: parseFloat(analytics.tourBooking.conversionRate) }
                        ]}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <defs>
                          <linearGradient id="colorRate" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="stage" stroke="#6b7280" style={{ fontSize: '12px' }} />
                        <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} unit="%" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                          }}
                          formatter={(value) => `${value.toFixed(1)}%`}
                        />
                        <Area
                          type="monotone"
                          dataKey="rate"
                          stroke="#8b5cf6"
                          strokeWidth={3}
                          fillOpacity={1}
                          fill="url(#colorRate)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                    <p className="text-xs text-gray-500 text-center mt-2">Conversion rate at each stage of the funnel</p>
                  </div>

                  {/* Services Status Donut Chart */}
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">🔄 Services Status</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Completed', value: analytics.otherServices.completed, color: '#10b981' },
                            { name: 'Assigned', value: analytics.otherServices.assigned - analytics.otherServices.completed, color: '#3b82f6' },
                            { name: 'Pending', value: analytics.otherServices.pending, color: '#f59e0b' }
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          fill="#8884d8"
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {[
                            { name: 'Completed', value: analytics.otherServices.completed, color: '#10b981' },
                            { name: 'Assigned', value: analytics.otherServices.assigned - analytics.otherServices.completed, color: '#3b82f6' },
                            { name: 'Pending', value: analytics.otherServices.pending, color: '#f59e0b' }
                          ].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                          }}
                        />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          iconType="circle"
                          wrapperStyle={{ fontSize: '12px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <p className="text-xs text-gray-500 text-center mt-2">Current status of other services leads</p>
                  </div>

                  {/* Comparison Bar Chart */}
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">⚖️ Performance Comparison</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={[
                          {
                            category: 'Tour Booking',
                            Total: analytics.tourBooking.total,
                            Successful: analytics.tourBooking.sentToClient,
                            Rate: parseFloat(analytics.tourBooking.conversionRate)
                          },
                          {
                            category: 'Other Services',
                            Total: analytics.otherServices.total,
                            Successful: analytics.otherServices.completed,
                            Rate: parseFloat(analytics.otherServices.completionRate)
                          }
                        ]}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="category" stroke="#6b7280" style={{ fontSize: '12px' }} />
                        <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                          }}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: '12px' }}
                          iconType="square"
                        />
                        <Bar dataKey="Total" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                        <Bar dataKey="Successful" fill="#10b981" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                    <p className="text-xs text-gray-500 text-center mt-2">Side-by-side comparison of tour booking vs other services</p>
                  </div>

                  {/* Radial Progress Chart */}
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">🎯 Success Metrics</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <RadialBarChart
                        cx="50%"
                        cy="50%"
                        innerRadius="10%"
                        outerRadius="90%"
                        barSize={20}
                        data={[
                          {
                            name: 'Overall Success',
                            value: parseFloat(analytics.overall.successRate),
                            fill: '#8b5cf6'
                          },
                          {
                            name: 'Tour Conversion',
                            value: parseFloat(analytics.tourBooking.conversionRate),
                            fill: '#3b82f6'
                          },
                          {
                            name: 'Service Completion',
                            value: parseFloat(analytics.otherServices.completionRate),
                            fill: '#10b981'
                          }
                        ]}
                        startAngle={180}
                        endAngle={0}
                      >
                        <RadialBar
                          minAngle={15}
                          label={{ position: 'insideStart', fill: '#fff', fontSize: 12 }}
                          background
                          clockWise
                          dataKey="value"
                        />
                        <Legend
                          iconSize={10}
                          layout="vertical"
                          verticalAlign="middle"
                          align="right"
                          wrapperStyle={{ fontSize: '11px' }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                          }}
                          formatter={(value) => `${value}%`}
                        />
                      </RadialBarChart>
                    </ResponsiveContainer>
                    <p className="text-xs text-gray-500 text-center mt-2">Radial view of success rates across all metrics</p>
                  </div>
                </div>
              </div>
            )}

            {/* Leads Table */}
            {(activeTab === 'tour_booking' || activeTab === 'other_services' || activeTab === 'forwarded') && (
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-700 border-b">
                  <h2 className="text-base font-bold text-white">
                    {activeTab === 'tour_booking' && '🎯 Tour Booking Leads'}
                    {activeTab === 'other_services' && '📋 Other Services Leads'}
                    {activeTab === 'forwarded' && '📤 Forwarded to Reservation'}
                  </h2>
                  <p className="text-indigo-100 text-xs mt-0.5">
                    {activeTab === 'tour_booking' && `${leads.filter(l => l.leadType === 'tour_booking').length} tour booking leads`}
                    {activeTab === 'other_services' && `${leads.filter(l => l.leadType === 'other_services').length} other services leads`}
                    {activeTab === 'forwarded' && `${forwardedLeads.length} leads with reservation team`}
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">SR No</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Destination</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Lead Type</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Created By</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                        {activeTab === 'other_services' && (
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Assigned To</th>
                        )}
                        {activeTab === 'other_services' && (
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                            <select
                              value={serviceTypeFilter}
                              onChange={(e) => setServiceTypeFilter(e.target.value)}
                              className="text-xs font-semibold bg-white border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="all">All Services</option>
                              <option value="visa">🛂 Visa</option>
                              <option value="passport">📘 Passport</option>
                              <option value="insurance">🛡️ Insurance</option>
                              <option value="hotel_booking">🏨 Hotel</option>
                              <option value="flight_booking">✈️ Flight</option>
                              <option value="other">📦 Other</option>
                            </select>
                          </th>
                        )}
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(activeTab === 'tour_booking' ? leads.filter(l => l.leadType === 'tour_booking') :
                        activeTab === 'other_services' ? leads.filter(l => {
                          const isOtherService = l.leadType === 'other_services';
                          const matchesServiceType = serviceTypeFilter === 'all' || l.serviceType === serviceTypeFilter;
                          return isOtherService && matchesServiceType;
                        }) :
                          forwardedLeads).map(lead => (
                            <tr key={lead._id} className={`transition-colors duration-150 ${activeTab === 'forwarded' ? 'bg-purple-50 hover:bg-purple-100' : 'hover:bg-gray-50'}`}>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <div className="flex items-center justify-center w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full shadow-md">
                                  <span className="text-xs font-bold text-white">
                                    {lead.serialNumber || '-'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <div>
                                  <button
                                    onClick={() => setSelectedLead(lead)}
                                    className="font-semibold text-blue-600 hover:text-blue-800 hover:underline text-left transition-colors text-sm"
                                  >
                                    {lead.customerName}
                                  </button>
                                  <div className="text-xs text-gray-500 mt-0.5">{lead.email}</div>
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <span className="text-gray-700 font-medium text-sm">{lead.destination || '-'}</span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${lead.leadType === 'tour_booking'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-teal-100 text-teal-800'
                                  }`}>
                                  {lead.leadType === 'tour_booking' ? '🎯 Tour' : '📋 Services'}
                                </span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center">
                                    <span className="text-xs font-bold text-blue-700">
                                      {(lead.createdByName || lead.assignedEmployeeName || 'Unknown').charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {lead.createdByName || lead.assignedEmployeeName || 'Unknown'}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString() : 'N/A'}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${activeTab === 'forwarded'
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-gray-100 text-gray-800'
                                  }`}>
                                  {lead.workflowStatus?.replace(/_/g, ' ') || 'New'}
                                </span>
                              </td>
                              {activeTab === 'other_services' && (
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {(() => {
                                    const employeeId = lead.assignedTo?._id || lead.assignedTo || lead.assignedEmployee?._id || lead.assignedEmployee;
                                    console.log('🔍 Lead:', lead.customerName, 'assignedTo:', lead.assignedTo, 'assignedEmployee:', lead.assignedEmployee, 'employeeId:', employeeId);

                                    if (employeeId) {
                                      const employeeName = getEmployeeName(employeeId);
                                      return (
                                        <div className="flex items-center gap-1.5">
                                          <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center">
                                            <span className="text-xs font-bold text-teal-700">
                                              {employeeName.charAt(0).toUpperCase()}
                                            </span>
                                          </div>
                                          <div>
                                            <p className="text-xs font-medium text-gray-900">
                                              {employeeName}
                                            </p>
                                            <p className="text-xs text-gray-500">Other Services</p>
                                          </div>
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-700">
                                          Not Assigned
                                        </span>
                                      );
                                    }
                                  })()}
                                </td>
                              )}
                              <td className="px-3 py-2 whitespace-nowrap">
                                <div className="flex gap-1 items-center">
                                  {(activeTab === 'tour_booking' || activeTab === 'other_services') && (
                                    <>
                                      {/* Assign button for Other Services leads */}
                                      {activeTab === 'other_services' && lead.leadType === 'other_services' && (
                                        <button
                                          onClick={() => {
                                            setSelectedLeadForAssign(lead);
                                            setShowAssignModal(true);
                                          }}
                                          className="px-2 py-1 bg-teal-600 text-white text-xs font-medium rounded hover:bg-teal-700 transition-all whitespace-nowrap"
                                          title="Assign to Other Services employee"
                                        >
                                          Assign
                                        </button>
                                      )}

                                      {/* Forward button for Tour Booking leads */}
                                      {activeTab === 'tour_booking' && (lead.currentOwner === 'sales' || !lead.currentOwner) && (
                                        <button
                                          onClick={() => forwardToReservation(lead._id)}
                                          className="px-2 py-1 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700 transition-all whitespace-nowrap"
                                        >
                                          Forward
                                        </button>
                                      )}

                                      <button
                                        onClick={() => {
                                          setSelectedLeadForFollowUp(lead);
                                          setShowFollowUpForm(true);
                                        }}
                                        className="px-2 py-1 bg-orange-600 text-white text-xs font-medium rounded hover:bg-orange-700 transition-all whitespace-nowrap"
                                        title="Create follow-up for this lead"
                                      >
                                        Follow-up
                                      </button>
                                      <button
                                        onClick={() => setEditingLead(lead)}
                                        className="px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-all whitespace-nowrap"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => handleDeleteLead(lead._id, lead.customerName)}
                                        className="px-2 py-1 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 transition-all whitespace-nowrap"
                                      >
                                        Delete
                                      </button>
                                    </>
                                  )}
                                  {activeTab === 'forwarded' && (
                                    <div className="flex flex-col gap-1">
                                      {/* Workflow Status */}
                                      <div className={`text-xs px-2 py-1 rounded-lg font-semibold inline-flex items-center gap-1 ${lead.workflowStatus === 'forwarded_to_sales' ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300' :
                                        lead.workflowStatus === 'quotation_prepared' ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-300' :
                                          lead.workflowStatus === 'reservation_working' ? 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border border-yellow-300' :
                                            'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border border-purple-300'
                                        }`}>
                                        {lead.workflowStatus === 'forwarded_to_sales' && '✅ Quotation Sent to Sales'}
                                        {lead.workflowStatus === 'quotation_prepared' && '📋 Quotation Prepared'}
                                        {lead.workflowStatus === 'reservation_working' && '⏳ Reservation Working'}
                                        {lead.workflowStatus === 'forwarded_to_reservation' && '📤 With Reservation'}
                                        {!['forwarded_to_sales', 'quotation_prepared', 'reservation_working', 'forwarded_to_reservation'].includes(lead.workflowStatus) &&
                                          `📍 ${lead.workflowStatus?.replace(/_/g, ' ')}`}
                                      </div>

                                      {/* Quotation Status if available */}
                                      {lead.quotations && lead.quotations.length > 0 && (
                                        <div className="text-xs text-gray-600">
                                          {lead.quotations.some(q => q.status === 'accepted') && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium">
                                              ✓ Accepted
                                            </span>
                                          )}
                                          {lead.quotations.some(q => q.status === 'rejected') && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded font-medium">
                                              ✗ Rejected
                                            </span>
                                          )}
                                          {lead.quotations.some(q => q.status === 'sent') && !lead.quotations.some(q => q.status === 'accepted' || q.status === 'rejected') && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">
                                              📤 {lead.quotations.length} Quotation(s)
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                    </tbody>
                  </table>
                </div>

                {/* Empty State */}
                {activeTab === 'tour_booking' && leads.filter(l => l.leadType === 'tour_booking').length === 0 && (
                  <div className="text-center py-8 px-4">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-2">
                      <span className="text-xl">🎯</span>
                    </div>
                    <p className="text-gray-700 text-base font-semibold mb-1">No tour booking leads</p>
                    <p className="text-gray-500 text-xs">Tour booking leads will appear here</p>
                  </div>
                )}
                {activeTab === 'other_services' && leads.filter(l => l.leadType === 'other_services').length === 0 && (
                  <div className="text-center py-8 px-4">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-teal-100 rounded-full mb-2">
                      <span className="text-xl">📋</span>
                    </div>
                    <p className="text-gray-700 text-base font-semibold mb-1">No other services leads</p>
                    <p className="text-gray-500 text-xs">Leads for passport, tickets, and other services will appear here</p>
                  </div>
                )}
                {activeTab === 'forwarded' && forwardedLeads.length === 0 && (
                  <div className="text-center py-8 px-4">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full mb-2">
                      <span className="text-xl">📤</span>
                    </div>
                    <p className="text-gray-700 text-base font-semibold mb-1">No forwarded leads</p>
                    <p className="text-gray-500 text-xs">Forward leads to reservation team to see them here</p>
                  </div>
                )}
              </div>
            )}

            {/* Quotations Table */}
            {/* Tour Booking Form - Add */}
            {showAddLead && (
              <TourBookingForm
                onClose={() => setShowAddLead(false)}
                onSuccess={() => {
                  fetchLeads();
                  setShowAddLead(false);
                }}
              />
            )}

            {/* Tour Booking Form - Edit */}
            {editingLead && (
              <TourBookingForm
                editLead={editingLead}
                onClose={() => setEditingLead(null)}
                onSuccess={() => {
                  fetchLeads();
                  setEditingLead(null);
                }}
              />
            )}

            {/* Quotations Table */}
            {activeTab === 'quotations' && (
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-700 border-b">
                  <h2 className="text-xl font-bold text-white">📄 Quotations from Reservation Team</h2>
                  <p className="text-blue-100 text-sm mt-1">
                    {quotations.length} quotation(s) received from reservation team
                  </p>
                </div>

                {loadingQuotations ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  </div>
                ) : quotations.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                      <span className="text-3xl">📄</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Quotations Yet</h3>
                    <p className="text-gray-600">
                      Quotations forwarded by the reservation team will appear here
                    </p>
                  </div>
                ) : (
                  <div style={{ overflow: 'visible' }}>
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quotation ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Customer
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Destination
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Sent Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Sent By
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {quotations.map((quotation) => (
                          <tr key={quotation._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                  <span className="text-lg">📄</span>
                                </div>
                                <div className="flex flex-col gap-1">
                                  <span className="font-mono text-sm font-semibold text-blue-600">
                                    {quotation.quotationNumber || quotation._id.substring(0, 8)}
                                  </span>
                                  {quotation.amendmentRequests && quotation.amendmentRequests.length > 0 && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full">
                                      🔄 AMENDED
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {quotation.customerName}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {quotation.customerEmail}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-900">
                                {quotation.destination || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm font-semibold text-green-600">
                                ₹{(quotation.items?.[0]?.totalPrice || quotation.packages?.[0]?.totalPrice || 0).toLocaleString()}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {quotation.sentDate
                                  ? new Date(quotation.sentDate).toLocaleDateString('en-IN')
                                  : 'N/A'}
                              </div>
                              <div className="text-xs text-gray-500">
                                {quotation.sentDate
                                  ? new Date(quotation.sentDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
                                  : ''}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                  <span className="text-xs font-bold text-indigo-700">
                                    {(quotation.sentByName || 'R').charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <span className="text-sm text-gray-900">
                                  {quotation.sentByName || 'Reservation Team'}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" style={{ position: 'relative', overflow: 'visible' }}>
                              <div className="quotation-dropdown-container" style={{ position: 'relative' }}>
                                {/* 3-Dot Menu Button */}
                                <button
                                  onClick={() => setOpenQuotationDropdown(openQuotationDropdown === quotation._id ? null : quotation._id)}
                                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                  <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                  </svg>
                                </button>

                                {/* Dropdown Menu */}
                                {openQuotationDropdown === quotation._id && (
                                  <div
                                    className="fixed bg-white rounded-lg shadow-2xl border border-gray-200 py-1"
                                    style={{
                                      zIndex: 9999,
                                      width: '200px',
                                      transform: 'translateX(-180px)',
                                      marginTop: '5px'
                                    }}
                                  >
                                    {/* View Quotation */}
                                    <button
                                      onClick={() => {
                                        setSelectedQuotationForView(quotation);
                                        setShowQuotationPDF(true);
                                        setOpenQuotationDropdown(null);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 flex items-center gap-2"
                                    >
                                      👁️ View Quotation
                                    </button>

                                    {quotation.status === 'sent' && (
                                      <>
                                        {/* Accept */}
                                        <button
                                          onClick={() => {
                                            handleAcceptQuotation(quotation);
                                            setOpenQuotationDropdown(null);
                                          }}
                                          className="w-full px-4 py-2 text-left text-sm text-green-700 hover:bg-green-50 flex items-center gap-2"
                                        >
                                          ✅ Accept Quotation
                                        </button>

                                        {/* Reject */}
                                        <button
                                          onClick={() => {
                                            handleRejectQuotation(quotation);
                                            setOpenQuotationDropdown(null);
                                          }}
                                          className="w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50 flex items-center gap-2"
                                        >
                                          ❌ Reject Quotation
                                        </button>

                                        {/* Request Amendment */}
                                        <button
                                          onClick={() => {
                                            setSelectedQuotationForRevision(quotation);
                                            setRevisionData({ changes: '', urgency: 'normal', notes: '' });
                                            setShowRevisionRequest(true);
                                            setOpenQuotationDropdown(null);
                                          }}
                                          className="w-full px-4 py-2 text-left text-sm text-orange-700 hover:bg-orange-50 flex items-center gap-2"
                                        >
                                          ✏️ Request Amendment
                                        </button>
                                      </>
                                    )}

                                    {/* Status Badge for non-sent */}
                                    {quotation.status !== 'sent' && (
                                      <div className="px-4 py-2 border-t border-gray-100">
                                        {quotation.status === 'accepted' && (
                                          <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded">✅ Accepted</span>
                                        )}
                                        {quotation.status === 'rejected' && (
                                          <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded">❌ Rejected</span>
                                        )}
                                        {quotation.status === 'amendment_requested' && (
                                          <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-1 rounded">🔄 Amendment Pending</span>
                                        )}
                                      </div>
                                    )}

                                    {/* Divider */}
                                    <div className="border-t border-gray-100 my-1"></div>

                                    {/* Delete */}
                                    <button
                                      onClick={() => {
                                        handleDeleteQuotation(quotation);
                                        setOpenQuotationDropdown(null);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                    >
                                      🗑️ Delete Quotation
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Follow-up Manager */}
            {activeTab === 'followUps' && (
              <FollowUpManager department="sales" leads={leads} />
            )}

            {/* Status Tracker - Cancelled & On-Hold Leads */}
            {activeTab === 'status_tracker' && (
              <div className="space-y-3">
                {/* Compact Summary Cards */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-3 rounded-lg shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs opacity-80">Cancelled</p>
                        <p className="text-2xl font-bold">
                          {leads.filter(l => l.workflowStatus === 'cancelled').length}
                        </p>
                      </div>
                      <span className="text-2xl opacity-40">❌</span>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white p-3 rounded-lg shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs opacity-80">On Hold</p>
                        <p className="text-2xl font-bold">
                          {leads.filter(l => l.workflowStatus === 'on_hold').length}
                        </p>
                      </div>
                      <span className="text-2xl opacity-40">⏸️</span>
                    </div>
                  </div>
                </div>

                {/* Status Tracker List - Compact */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-4 py-2.5 bg-gray-700 text-white flex items-center justify-between">
                    <h3 className="text-sm font-semibold">📋 Cancelled & On-Hold Leads</h3>
                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded">
                      {leads.filter(l => l.workflowStatus === 'cancelled' || l.workflowStatus === 'on_hold').length} total
                    </span>
                  </div>

                  {leads.filter(l => l.workflowStatus === 'cancelled' || l.workflowStatus === 'on_hold').length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-3xl mb-2">✅</div>
                      <p className="text-gray-600 font-medium">No Cancelled or On-Hold Leads</p>
                      <p className="text-gray-400 text-xs">All leads are active!</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {leads
                        .filter(l => l.workflowStatus === 'cancelled' || l.workflowStatus === 'on_hold')
                        .sort((a, b) => new Date(b.statusChangedAt || b.updatedAt) - new Date(a.statusChangedAt || a.updatedAt))
                        .map(lead => (
                          <div
                            key={lead._id}
                            className={`p-3 hover:bg-gray-50 transition-colors ${lead.workflowStatus === 'cancelled' ? 'border-l-3 border-red-500' : 'border-l-3 border-yellow-500'
                              }`}
                            style={{ borderLeftWidth: '3px' }}
                          >
                            <div className="flex items-start justify-between gap-3">
                              {/* Left: Lead Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`px-2 py-0.5 text-xs font-semibold rounded ${lead.workflowStatus === 'cancelled'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-yellow-100 text-yellow-700'
                                    }`}>
                                    {lead.workflowStatus === 'cancelled' ? '✕ CANCELLED' : '⏸ ON HOLD'}
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    {lead.leadType === 'other_services'
                                      ? lead.serviceType?.replace(/_/g, ' ').toUpperCase() || 'OTHER'
                                      : 'TOUR'}
                                  </span>
                                </div>
                                <h4 className="font-semibold text-gray-800 text-sm truncate">{lead.customerName}</h4>
                                <p className="text-xs text-gray-500 truncate">
                                  {lead.email || '-'} • {lead.contactNumber || '-'}
                                </p>
                              </div>

                              {/* Right: Reason */}
                              <div className="w-48 flex-shrink-0">
                                <p className="text-xs text-gray-400 mb-0.5">
                                  {lead.workflowStatus === 'cancelled' ? 'Reason:' : 'Hold Reason:'}
                                </p>
                                <p className="text-xs text-gray-700 font-medium line-clamp-2">
                                  {lead.statusChangeReason || 'No reason'}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {lead.statusChangedBy || '-'} • {
                                    lead.statusChangedAt
                                      ? new Date(lead.statusChangedAt).toLocaleDateString('en-IN', {
                                        day: '2-digit',
                                        month: 'short',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })
                                      : '-'
                                  }
                                </p>
                              </div>

                              {/* View Button */}
                              <button
                                onClick={() => setSelectedLead(lead)}
                                className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 flex-shrink-0"
                              >
                                View
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Create Follow-up Modal for Specific Lead */}
            {showFollowUpForm && selectedLeadForFollowUp && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                  <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-yellow-600 text-white px-6 py-4 flex justify-between items-center">
                    <div>
                      <h2 className="text-2xl font-bold">📅 Create Follow-up</h2>
                      <p className="text-sm opacity-90">For: {selectedLeadForFollowUp.customerName}</p>
                    </div>
                    <button
                      onClick={() => {
                        setShowFollowUpForm(false);
                        setSelectedLeadForFollowUp(null);
                      }}
                      className="text-white hover:text-gray-200 text-2xl"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="p-6">
                    <FollowUpManager
                      department="sales"
                      leadId={selectedLeadForFollowUp._id}
                      customerName={selectedLeadForFollowUp.customerName}
                      customerPhone={selectedLeadForFollowUp.contactNumber}
                      customerEmail={selectedLeadForFollowUp.email}
                      showOnlyForm={true}
                      onSuccess={() => {
                        setShowFollowUpForm(false);
                        setSelectedLeadForFollowUp(null);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Lead Detail Modal */}
            {selectedLead && (
              <LeadDetailModal
                lead={selectedLead}
                onClose={() => setSelectedLead(null)}
              />
            )}

            {/* Old Add Lead Modal - REMOVED */}
            {false && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                  <h2 className="text-2xl font-bold mb-4">Add New Lead</h2>
                  <form onSubmit={handleAddLead}>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Customer Name *</label>
                        <input
                          type="text"
                          required
                          value={newLead.customerName}
                          onChange={(e) => setNewLead({ ...newLead, customerName: e.target.value })}
                          className="w-full border rounded px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Contact Number *</label>
                        <input
                          type="text"
                          required
                          value={newLead.contactNumber}
                          onChange={(e) => setNewLead({ ...newLead, contactNumber: e.target.value })}
                          className="w-full border rounded px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Email *</label>
                        <input
                          type="email"
                          required
                          value={newLead.email}
                          onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                          className="w-full border rounded px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Destination</label>
                        <input
                          type="text"
                          value={newLead.destination}
                          onChange={(e) => setNewLead({ ...newLead, destination: e.target.value })}
                          className="w-full border rounded px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Number of Days</label>
                        <input
                          type="number"
                          value={newLead.numberOfDays}
                          onChange={(e) => setNewLead({ ...newLead, numberOfDays: e.target.value })}
                          className="w-full border rounded px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Travel Date</label>
                        <input
                          type="date"
                          value={newLead.travelDate}
                          onChange={(e) => setNewLead({ ...newLead, travelDate: e.target.value })}
                          className="w-full border rounded px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Budget</label>
                        <input
                          type="text"
                          value={newLead.budget}
                          onChange={(e) => setNewLead({ ...newLead, budget: e.target.value })}
                          className="w-full border rounded px-3 py-2"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium mb-1">Notes</label>
                        <textarea
                          value={newLead.notes}
                          onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                          className="w-full border rounded px-3 py-2"
                          rows="3"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-6">
                      <button
                        type="button"
                        onClick={() => setShowAddLead(false)}
                        className="px-4 py-2 border rounded hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Add Lead
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Quotation PDF View Modal */}
            {showQuotationPDF && selectedQuotationForView && (
              <TourQuotationPDF
                quotation={selectedQuotationForView}
                lead={selectedQuotationForView.lead}
                onClose={() => {
                  setShowQuotationPDF(false);
                  setSelectedQuotationForView(null);
                }}
                showDownloadButton={true}
              />
            )}

            {/* Revision Request Modal */}
            {showRevisionRequest && selectedQuotationForRevision && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                  <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-red-600 text-white px-6 py-4 flex justify-between items-center">
                    <div>
                      <h2 className="text-2xl font-bold">🔄 Quotation Amendment Request</h2>
                      <p className="text-sm opacity-90 mt-1">
                        {selectedQuotationForRevision.tourCode || selectedQuotationForRevision.customerName} - Request modifications from Reservation Team
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setShowRevisionRequest(false);
                        setSelectedQuotationForRevision(null);
                        setRevisionData({ changes: '', urgency: 'normal', notes: '' });
                      }}
                      className="text-white hover:text-gray-200 text-2xl"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="p-6">
                    {/* Quotation Info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <h3 className="font-semibold text-blue-900 mb-2">Quotation Details</h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {selectedQuotationForRevision.tourCode && (
                          <div className="col-span-2 bg-blue-100 p-2 rounded border border-blue-300">
                            <span className="text-blue-700 font-medium">🎫 Tour Code:</span>
                            <span className="ml-2 font-bold text-blue-900 font-mono">
                              {selectedQuotationForRevision.tourCode}
                            </span>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-600">Quotation ID:</span>
                          <span className="ml-2 font-semibold text-gray-900">
                            {selectedQuotationForRevision.quotationNumber || selectedQuotationForRevision._id.substring(0, 8)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Customer:</span>
                          <span className="ml-2 font-semibold text-gray-900">
                            {selectedQuotationForRevision.customerName}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Destination:</span>
                          <span className="ml-2 font-semibold text-gray-900">
                            {selectedQuotationForRevision.items?.[0]?.destination ||
                              selectedQuotationForRevision.destination ||
                              'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Amount:</span>
                          <span className="ml-2 font-semibold text-green-600">
                            ₹{(
                              selectedQuotationForRevision.items?.[0]?.totalPrice ||
                              selectedQuotationForRevision.packages?.[0]?.totalPrice ||
                              selectedQuotationForRevision.totalPrice ||
                              selectedQuotationForRevision.amount ||
                              0
                            ).toLocaleString()}
                          </span>
                        </div>
                        {/* Duration */}
                        {(selectedQuotationForRevision.items?.[0]?.duration || selectedQuotationForRevision.duration) && (
                          <div>
                            <span className="text-gray-600">Duration:</span>
                            <span className="ml-2 font-semibold text-gray-900">
                              {selectedQuotationForRevision.items?.[0]?.duration || selectedQuotationForRevision.duration}
                            </span>
                          </div>
                        )}
                        {/* Pax */}
                        {(selectedQuotationForRevision.items?.[0]?.totalPax || selectedQuotationForRevision.totalPax) && (
                          <div>
                            <span className="text-gray-600">Pax:</span>
                            <span className="ml-2 font-semibold text-gray-900">
                              {selectedQuotationForRevision.items?.[0]?.totalPax || selectedQuotationForRevision.totalPax}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Revision Form */}
                    <form onSubmit={async (e) => {
                      e.preventDefault();

                      if (!revisionData.changes.trim()) {
                        alert('Please describe the amendments required');
                        return;
                      }

                      try {
                        const token = localStorage.getItem('token') ||
                          localStorage.getItem('companyToken') ||
                          localStorage.getItem('employeeToken');

                        const response = await axios.post(
                          `/leads/${selectedQuotationForRevision.leadId}/request-quotation-revision`,
                          {
                            quotationId: selectedQuotationForRevision._id,
                            changes: revisionData.changes,
                            urgency: revisionData.urgency,
                            notes: revisionData.notes,
                            requestedBy: user?.id || user?._id,
                            requestedByName: user?.name || 'Sales Team'
                          },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );

                        if (response.data.success) {
                          alert('✅ Amendment request submitted successfully to Reservation Team!');
                          setShowRevisionRequest(false);
                          setSelectedQuotationForRevision(null);
                          setRevisionData({ changes: '', urgency: 'normal', notes: '' });
                          refreshQuotations(); // Refresh list
                        } else {
                          alert('Failed to submit amendment request');
                        }
                      } catch (error) {
                        console.error('Error sending revision request:', error);
                        alert('Error: ' + (error.response?.data?.message || error.message));
                      }
                    }}>
                      {/* Amendment Details */}
                      <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Amendment Details *
                        </label>
                        <textarea
                          value={revisionData.changes}
                          onChange={(e) => setRevisionData({ ...revisionData, changes: e.target.value })}
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          rows="4"
                          placeholder="Describe the amendments requested by client...&#10;Example:&#10;- Upgrade hotel from 3-star to 4-star&#10;- Extend itinerary by 1 additional day&#10;- Modify room configuration from 3 to 2 rooms"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Please specify all modifications required
                        </p>
                      </div>

                      {/* Urgency */}
                      <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Urgency Level *
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                          <label className={`flex items-center justify-center p-3 border-2 rounded-lg cursor-pointer transition-all ${revisionData.urgency === 'low'
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-300 hover:border-green-300'
                            }`}>
                            <input
                              type="radio"
                              name="urgency"
                              value="low"
                              checked={revisionData.urgency === 'low'}
                              onChange={(e) => setRevisionData({ ...revisionData, urgency: e.target.value })}
                              className="mr-2"
                            />
                            <span className="font-medium">🟢 Low</span>
                          </label>

                          <label className={`flex items-center justify-center p-3 border-2 rounded-lg cursor-pointer transition-all ${revisionData.urgency === 'normal'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-blue-300'
                            }`}>
                            <input
                              type="radio"
                              name="urgency"
                              value="normal"
                              checked={revisionData.urgency === 'normal'}
                              onChange={(e) => setRevisionData({ ...revisionData, urgency: e.target.value })}
                              className="mr-2"
                            />
                            <span className="font-medium">🔵 Normal</span>
                          </label>

                          <label className={`flex items-center justify-center p-3 border-2 rounded-lg cursor-pointer transition-all ${revisionData.urgency === 'high'
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-300 hover:border-red-300'
                            }`}>
                            <input
                              type="radio"
                              name="urgency"
                              value="high"
                              checked={revisionData.urgency === 'high'}
                              onChange={(e) => setRevisionData({ ...revisionData, urgency: e.target.value })}
                              className="mr-2"
                            />
                            <span className="font-medium">🔴 High</span>
                          </label>
                        </div>
                      </div>

                      {/* Additional Notes */}
                      <div className="mb-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Additional Notes (Optional)
                        </label>
                        <textarea
                          value={revisionData.notes}
                          onChange={(e) => setRevisionData({ ...revisionData, notes: e.target.value })}
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          rows="3"
                          placeholder="Any additional context or information..."
                        />
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3 justify-end pt-4 border-t">
                        <button
                          type="button"
                          onClick={() => {
                            setShowRevisionRequest(false);
                            setSelectedQuotationForRevision(null);
                            setRevisionData({ changes: '', urgency: 'normal', notes: '' });
                          }}
                          className="px-6 py-2.5 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-all font-medium"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-6 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 transition-all font-medium shadow-md"
                        >
                          📤 Submit Amendment Request
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Reject Quotation Modal */}
            {showRejectModal && selectedQuotationForReject && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg w-full max-w-md">
                  <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-4 flex justify-between items-center rounded-t-lg">
                    <div>
                      <h2 className="text-xl font-bold">✕ Reject Quotation</h2>
                      <p className="text-sm opacity-90 mt-1">
                        Provide reason for rejection
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setShowRejectModal(false);
                        setSelectedQuotationForReject(null);
                        setRejectReason('');
                      }}
                      className="text-white hover:text-gray-200 text-2xl"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="p-6">
                    {/* Quotation Info */}
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <div className="text-sm">
                        <div className="mb-2">
                          <span className="text-gray-600">Customer:</span>
                          <span className="ml-2 font-semibold text-gray-900">
                            {selectedQuotationForReject.customerName}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Amount:</span>
                          <span className="ml-2 font-semibold text-red-600">
                            ₹{(selectedQuotationForReject.amount || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Rejection Reason */}
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Reason for Rejection *
                      </label>
                      <textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        rows="4"
                        placeholder="Please explain why you're rejecting this quotation..."
                        required
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          setShowRejectModal(false);
                          setSelectedQuotationForReject(null);
                          setRejectReason('');
                        }}
                        className="px-6 py-2.5 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-all font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={submitRejectQuotation}
                        className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all font-medium shadow-md"
                      >
                        ✕ Reject Quotation
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div >
      </div >
    </div >
  );
};

export default SalesDashboard;
